// Wechsel auf einen ANDEREN Tab derselben App im VARIO-Host.
//
// Warum das nötig ist (Betreiber-Befund 2026-08-28): Der Fehler-Dialog im Bestellungen-Tab
// verlinkt in die Einstellungen. Bisher navigierte die App dafür das iframe selbst
// (`window.location` → `/#/settings`). Inhaltlich landet man richtig, aber die Tab-Leiste oben
// gehört VARIO, nicht uns — sie blieb auf „Bestellungen" stehen, während darunter die
// Einstellungen standen. Jeder Tab ist eine eigene Integration
// (`settings.sales-channels.{appId}.tabs`), der Host weiß von unserer internen Navigation nichts.
//
// VARIO hat dafür eine dokumentierte Nachricht: `openInSameTab` mit einer vue-router-Location.
// Zwei Angaben aus der Doku (developer.vario-software.de, „UI integration") tragen das hier:
//   1. App-Integrationen bekommen generierte Routennamen `apps.<appIdentifier>.<integrationId>`.
//   2. Auf der iframe-URL stehen neben den Framework-Parametern auch `routeName` UND **jeder
//      Parameter der aktuellen ERP-Route** — daher kommt die Verkaufskanal-ID, ohne dass wir sie
//      erraten oder nachladen müssten.
//
// Vom Framework belegte Parameter — alles ANDERE auf der Query ist ein Parameter der aktuellen
// ERP-Route und gehört ans Ziel weitergereicht. `appToken`/`offlineToken` sind unsere eigenen
// Handshake-Parameter und dürfen NICHT als Routen-Parameter mitgehen.
const FRAMEWORK_PARAMS: ReadonlySet<string> = new Set([
  'appIdentifier', 'integrationId', 'language', 'supportMode', 'myCompanyId', 'uimode',
  'detailDatagridTable', 'detailDatagrid', 'superUser', 'permissions', 'additionalPayload',
  'userId', 'routeName',
  'appToken', 'offlineToken', 'gambioSettingsSection',
]);

export interface VarioRouteTarget {
  name: string;
  params: Record<string, string>;
}

/**
 * Baut die vue-router-Location für eine Schwester-Integration am SELBEN Integrationspunkt
 * (z. B. Verkaufskanal-Tab → Verkaufskanal-Tab): generierter Routenname plus alle Parameter der
 * aktuellen ERP-Route, denn das Ziel braucht dieselbe Verkaufskanal-ID.
 *
 * `null`, wenn die App nicht im VARIO-Host läuft (kein `appIdentifier` auf der URL, z. B.
 * standalone im Browser) — dann bleibt dem Aufrufer die interne Navigation.
 */
export function buildAppRouteTarget(search: string, integrationId: string): VarioRouteTarget | null {
  const q = new URLSearchParams(search);
  const appIdentifier = q.get('appIdentifier');
  if (!appIdentifier || !integrationId) return null;
  const params: Record<string, string> = {};
  for (const [key, value] of q.entries()) {
    if (!FRAMEWORK_PARAMS.has(key)) params[key] = value;
  }
  return { name: `apps.${appIdentifier}.${integrationId}`, params };
}

/**
 * ERP-Route eines App-Tabs in der Appverwaltung (System → Apps → App → Reiter), also einer
 * Integration an `system.apps.{appId}.tabs`. Seit 2026-09-14 liegt dort `gambio-settings`.
 *
 * Name und Parameter sind NICHT geraten, sondern am 2026-09-15 aus dem `additionalPayload` des
 * echten Einstellungen-iframes abgelesen (goecommerce.vario.cloud):
 *   `{ name: 'system.apps.detail.app', params: { identifier, appIdentifier, integrationId } }`
 * `identifier` und `appIdentifier` tragen beide die App-ID. Zum Vergleich der Verkaufskanal-Tab:
 *   `{ name: 'saleschannels.saleschannel.app', params: { channelType, id, appIdentifier, integrationId } }`
 *
 * Warum nicht der generierte Name `apps.<appIdentifier>.<integrationId>` wie oben: ohne die
 * Verkaufskanal-Parameter hat der Host ihn im Live-Test ignoriert (synthetische `openInSameTab`-
 * Nachricht aus dem Bestellungen-Tab, URL blieb stehen); mit der echten Route wechselte er sofort
 * auf die App-Detailseite. Die Verkaufskanal-Parameter dürfen hier nicht mit: `id` bedeutet an der
 * App-Route etwas anderes.
 */
export function buildAppManagementRouteTarget(search: string, integrationId: string): VarioRouteTarget | null {
  const appIdentifier = new URLSearchParams(search).get('appIdentifier');
  if (!appIdentifier || !integrationId) return null;
  return {
    name: 'system.apps.detail.app',
    params: { identifier: appIdentifier, appIdentifier, integrationId },
  };
}

/**
 * Schlüssel, unter dem der Bestellungen-Tab die gewünschte Einstellungen-Sektion hinterlegt,
 * bevor er den VARIO-Host den Reiter wechseln lässt. Beim Host-Wechsel setzt VARIO die
 * iframe-URL — unser `?gambioSettingsSection=` überlebt das nicht, `sessionStorage` schon
 * (gleicher Origin). Die Einstellungen-Seite liest ihn EINMAL und räumt ihn weg.
 */
export const SETTINGS_SECTION_HANDOVER = 'gambio:settingsSection';
